import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    forwardRef,
    Input,
    OnChanges,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import {NG_VALIDATORS, NG_VALUE_ACCESSOR} from '@angular/forms';
import {hasValue} from 'app/core/helpers/array';
import {CheckboxTreeNode, CheckboxTreeNodes, CheckboxTreePresets} from 'app/shared/components/checkbox-tree/checkbox-tree.interface';
import {CustomFormComponent} from '../../classes/custom-form-component';
import {OverlayElementEditor} from '../../classes/overlay-element-editor';
import {CheckboxTreeComponent} from '../checkbox-tree/checkbox-tree.component';
import {DropDownComponent} from '../drop-down/drop-down.component';
import {EnumOutputComponent} from '../enum-output/enum-output.component';
import {SearchInputComponent} from '../search-input/search-input.component';

@Component({
    selector: 'drop-down-picker',
    templateUrl: './drop-down-picker.component.html',
    styleUrls: ['./drop-down-picker.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DropDownPickerComponent),
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => DropDownPickerComponent),
            multi: true,
        },
    ],
})
export class DropDownPickerComponent extends CustomFormComponent<Set<any>> implements OnChanges {
    static EMPTY_PLACEHOLDER = 'Nothing selected';
    static FULL_PLACEHOLDER = 'All selected';
    @Input() options: CheckboxTreeNodes;
    @Input() presets: CheckboxTreePresets = [];
    @Input() selectAllPreset: {label: string};
    @Input() searchPlaceholder: string;
    @Input() noResultsPlaceholder: string;
    @Input() emptyPlaceholder: string;
    @Input() fullPlaceholder: string;
    @Input() fullPlaceholderEnabled = true;
    @Input() valid = true;
    @Input() restStringFn: (restCount: number) => string;
    @ViewChild('dropDown', {static: true}) dropDown: DropDownComponent;
    @ViewChild('enumOutput', {static: false}) enumOutput: EnumOutputComponent;
    @ViewChild('searchInput', {static: false}) searchInput: SearchInputComponent;
    @ViewChild('treeComponent', {static: false}) treeComponent: CheckboxTreeComponent;

    searchQuery: string;
    private nodes = new Map<any, CheckboxTreeNode>();

    trackByFn = (index, value) => value;

    get selected(): Set<any> {
        if (!this.internalValue) {
            this.selected = new Set();
        }
        return this.internalValue;
    }

    set selected(v: Set<any>) {
        this.internalValue = v;
    }

    get emptyPlaceholderText() {
        return this.emptyPlaceholder || DropDownPickerComponent.EMPTY_PLACEHOLDER;
    }

    get fullPlaceholderText() {
        return this.fullPlaceholder || DropDownPickerComponent.FULL_PLACEHOLDER;
    }

    constructor($el: ElementRef, cd: ChangeDetectorRef) {
        super($el, cd);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.options) {
            this.initInputValue('options', []);
            this.createNodesMap();
        }
    }

    onRender(editor: OverlayElementEditor) {
        let wrapper = editor.getElement().firstElementChild as HTMLElement;
        editor.setStyleProperties(wrapper, {
            '--width-expanded': 'var(--width-collapsed)',
        });
        if (this.enumOutput) {
            this.enumOutput.onEnumSizeChange();
        }
    }

    onOptionsToggle(state: boolean) {
        this.cd.detectChanges();
        if (state && this.searchInput) {
            this.searchInput.getInput().focus();
        } else {
            this.treeComponent = null;
            this.searchQuery = '';
        }
    }

    onSearch() {
        this.fitDropDownHeight();
    }

    onExpand() {
        this.fitDropDownHeight();
    }

    onClear(event: MouseEvent) {
        event.stopPropagation();
        this.selected.clear();
        if (this.treeComponent) {
            this.treeComponent.tree.changeSelected(this.selected);
            this.treeComponent.cd.markForCheck();
        }
    }

    getNodeByValue(value: any): CheckboxTreeNode {
        return this.nodes.get(value);
    }

    getLabels(): Array<string> {
        return [...this.selected.values()]
            .map(value => this.getNodeByValue(value))
            .filter(hasValue)
            .map(node => node.label);
    }

    isAllSelected(): boolean {
        return this.fullPlaceholderEnabled && this.selected.size && this.selected.size === this.nodes.size;
    }

    private fitDropDownHeight() {
        requestAnimationFrame(() => this.dropDown.updateHeight());
    }

    private createNodesMap() {
        let extract = (nodes: CheckboxTreeNodes) => {
            for (let node of nodes) {
                if (Array.isArray(node.children) && node.children.length) {
                    extract(node.children);
                } else {
                    this.nodes.set(node.value, node);
                }
            }
        };
        extract(this.options);
    }
}
